/* eslint-disable react-refresh/only-export-components -- TanStack Router: Route + co-located page */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

import { DocsPageContent } from "@/presentation/components/docs/docs-page-content";
import { DocsSidebarNav } from "@/presentation/components/docs/docs-sidebar-nav";
import { DocsToc } from "@/presentation/components/docs/docs-toc";
import { PageHeader } from "@/presentation/components/shell/page-header";
import {
  defaultDocsSlug,
  docsSections,
  findDocsPage,
} from "@/presentation/data/docs/docs-manifest";

const searchSchema = z.object({
  page: z.string().optional(),
});

export const Route = createFileRoute("/_app/docs")({
  validateSearch: searchSchema,
  component: DocsRoute,
});

function DocsRoute() {
  const navigate = Route.useNavigate();
  const { page: pageSlug } = Route.useSearch();
  const requestedSlug = pageSlug ?? defaultDocsSlug;
  const page = findDocsPage(requestedSlug) ?? findDocsPage(defaultDocsSlug);
  // Derive the active slug from the page actually rendered so nav + content
  // agree even when `?page=` is an unknown slug (falls back to the default).
  const activeSlug = page?.slug ?? defaultDocsSlug;

  // Reset scroll when switching pages so the reader starts at the top.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [activeSlug]);

  if (!page) return null;

  return (
    <div className="animate-page-enter flex w-full flex-col">
      <PageHeader
        eyebrow="Yango Platform"
        title="Docs"
        summary="Living reference for the platform ecosystem — architecture, environments, API integration and customizations."
      />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-8 px-6 py-8 lg:grid-cols-[15rem_minmax(0,1fr)] lg:px-10 xl:grid-cols-[15rem_minmax(0,1fr)_13rem]">
        <aside className="hidden lg:block">
          <div className="sticky top-6">
            <DocsSidebarNav activeSlug={activeSlug} />
          </div>
        </aside>
        <main className="min-w-0">
          {/* Sub-lg: the sidebar is hidden, so offer a labelled page picker. */}
          <div className="mb-6 lg:hidden">
            <label
              htmlFor="docs-page-select"
              className="text-muted-foreground mb-1.5 block text-xs font-medium"
            >
              Docs page
            </label>
            <select
              id="docs-page-select"
              value={activeSlug}
              onChange={(e) => void navigate({ search: { page: e.target.value } })}
              className="border-input bg-background text-foreground focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-1 focus-visible:outline-none"
            >
              {docsSections.map((section) => (
                <optgroup key={section.label} label={section.label}>
                  {section.pages.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.title}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <DocsPageContent page={page} />
        </main>
        <aside className="hidden xl:block">
          <div className="sticky top-6">
            <DocsToc page={page} />
          </div>
        </aside>
      </div>
    </div>
  );
}
